import { assertNever } from '../assert-never';
import type { DimensionText } from '../dimensions';
import { arcPointAt, arcSweep, type Bounds, type Shape, type Vec2 } from '../geom';
import { LINE_TYPES, type Style, type Unit } from '../model';
import { layoutsOf, sceneBounds, type Drawn, type ResolvedLayout } from '../scene';

export interface SvgOptions {
  readonly strokeWidth?: number;
  readonly padding?: number;
  /** The world region to show, exactly; otherwise the drawing's bounds plus padding. */
  readonly bounds?: Bounds;
  /**
   * Draws a view, as the canvas does: dashes are measured in pixels of this many world
   * units, and a style's thickness is never thinner than one.
   */
  readonly unitsPerPixel?: number;
  /** What the numbers mean, so the page comes out its physical size; left out, it is pixels. */
  readonly unit?: Unit;
}

/** Each unit as an SVG length unit and how many of those one makes; SVG has no feet or metres. */
const SVG_UNITS: Record<Unit, readonly [string, number]> = {
  mm: ['mm', 1],
  cm: ['cm', 1],
  m: ['cm', 100],
  in: ['in', 1],
  ft: ['in', 12],
};

/**
 * Dimensions draw their strokes, closed arrowheads and text in their color; text is a
 * sans-serif `<text>`, so its exact width depends on the viewer's font.
 */
export function toSvg(drawn: Drawn, options: SvgOptions = {}): string {
  const padding = options.padding ?? 10;
  const strokeWidth = options.strokeWidth ?? 0.5;
  const box =
    options.bounds ??
    padded(sceneBounds(drawn) ?? { min: { x: 0, y: 0 }, max: { x: 100, y: 100 } }, padding);
  const width = box.max.x - box.min.x;
  const height = box.max.y - box.min.y;
  const [suffix, per] = options.unit ? SVG_UNITS[options.unit] : ['', 1];
  const size = (length: number) => `${n(length * per)}${suffix}`;

  const paths = [
    ...drawn.entities
      .filter((entity) => entity.visible)
      .map(
        (entity) =>
          `    <path d="${pathData(entity.shape)}"${strokeAttributes(entity.style, options.unitsPerPixel)} />`,
      ),
    ...layoutsOf(drawn)
      .filter((annotation) => annotation.visible)
      .map((annotation) => layoutMarkup(annotation, options.unitsPerPixel)),
  ].join('\n');

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${box.min.x} ${-box.max.y} ${width} ${height}" width="${size(width)}" height="${size(height)}">`,
    `  <g fill="none" stroke="black" stroke-width="${strokeWidth}" transform="scale(1,-1)">`,
    paths,
    '  </g>',
    '</svg>',
    '',
  ].join('\n');
}

/** Only what differs from the group's black stroke of the default width. */
function strokeAttributes(style: Style | undefined, unitsPerPixel: number | undefined): string {
  if (!style) return '';
  let attributes = '';
  if (style.color) attributes += ` stroke="${style.color}"`;
  if (style.thick !== undefined)
    attributes += ` stroke-width="${n(Math.max(style.thick, unitsPerPixel ?? 0))}"`;
  const pattern = LINE_TYPES[style.line ?? 'solid'].pattern;
  if (pattern.length > 0) {
    const dashes = pattern.map((length) => n(length * (unitsPerPixel ?? 1)));
    attributes += ` stroke-dasharray="${dashes.join(' ')}"`;
  }
  return attributes;
}

function layoutMarkup(annotation: ResolvedLayout, unitsPerPixel: number | undefined): string {
  const { strokes, fills, text } = annotation.layout;
  const color = annotation.style?.color ?? 'black';
  const lines = [
    `    <g${strokeAttributes(annotation.style, unitsPerPixel)}>`,
    `      <path d="${strokes.map(pathData).join(' ')}" />`,
    ...fills.map(
      (polygon) => `      <path d="${polygonData(polygon)}" fill="${color}" stroke="none" />`,
    ),
    `      ${textMarkup(text, color)}`,
    '    </g>',
  ];
  return lines.join('\n');
}

function polygonData(points: readonly Vec2[]): string {
  return `${points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${n(p.x)} ${n(p.y)}`).join(' ')} Z`;
}

/** The drawing is flipped so y points up, so text is flipped back to stand upright. */
function textMarkup(text: DimensionText, color: string): string {
  const transform = `translate(${n(text.at.x)} ${n(text.at.y)}) scale(1 -1) rotate(${n(-text.angle)})`;
  return `<text transform="${transform}" font-family="sans-serif" font-size="${n(text.height)}" text-anchor="middle" dominant-baseline="central" fill="${color}" stroke="none">${escapeText(text.content)}</text>`;
}

const escapeText = (content: string): string =>
  content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function pathData(shape: Shape): string {
  switch (shape.type) {
    case 'line':
      return `M ${n(shape.a.x)} ${n(shape.a.y)} L ${n(shape.b.x)} ${n(shape.b.y)}`;
    case 'circle': {
      const { center, radius } = shape;
      return (
        `M ${n(center.x - radius)} ${n(center.y)} ` +
        `A ${n(radius)} ${n(radius)} 0 1 0 ${n(center.x + radius)} ${n(center.y)} ` +
        `A ${n(radius)} ${n(radius)} 0 1 0 ${n(center.x - radius)} ${n(center.y)}`
      );
    }
    case 'arc': {
      const start = arcPointAt(shape, 0);
      const end = arcPointAt(shape, 1);
      const large = arcSweep(shape) > 180 ? 1 : 0;
      return (
        `M ${n(start.x)} ${n(start.y)} ` +
        `A ${n(shape.radius)} ${n(shape.radius)} 0 ${large} 1 ${n(end.x)} ${n(end.y)}`
      );
    }
    default:
      return assertNever(shape);
  }
}

function padded(box: Bounds, padding: number): Bounds {
  return {
    min: { x: box.min.x - padding, y: box.min.y - padding },
    max: { x: box.max.x + padding, y: box.max.y + padding },
  };
}

const n = (value: number): string => String(Number(value.toFixed(6)));
